
import { VNodeChildren } from 'vue';
import { PropType, defineComponent, h } from '@vue/composition-api';

import { Group } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';

import {
  IFieldErrors,
  IWorkflowTransitionNode,
  TransitionNodeBinaryOperators,
  TransitionNodeOperandSubtype,
  WorkflowTransitionNodeOperandTypeInfo,
  WorkflowTransitionNode,
  WorkflowTransitionNodeOperatorInfo,
  IWorkflowState
} from '@/models';
import { PredicateNodeType } from '@/models/predicate/predicate.constant';
import { ExpressionPredicateNodeTree } from '@/models/predicate/tree';

import { requiredValidator } from '@/utils/validators';

import { useProxiedModel } from '@/composable/proxymodel';

import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import WorkflowStateThresholdGroupAutoPicker from '../WorkflowStateThresholdGroupAutoPicker.vue';

export default defineComponent({
  name: 'WorkflowTransitionPredicateQueryTree',

  props: {
    value: {
      type: Array as PropType<
        ExpressionPredicateNodeTree<IWorkflowTransitionNode>
      >,
      default: () => []
    },
    fieldErrors: {
      type: Array as PropType<IFieldErrors[]>,
      default: (): IFieldErrors[] => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    level: {
      type: Number,
      default: 0
    }
  },

  emits: ['input'],

  setup(props, { emit }) {
    const nodes = useProxiedModel(props, 'value');

    function logicalOperatorStringRepresentation(
      subtype: TransitionNodeBinaryOperators
    ) {
      switch (subtype) {
        case TransitionNodeBinaryOperators.And:
          return 'And';
        case TransitionNodeBinaryOperators.Or:
          return 'Or';
        default:
          return 'Unknown';
      }
    }

    function setNodeProperty<T = IWorkflowTransitionNode>(
      node: T,
      field: keyof T,
      value: T[keyof T]
    ) {
      node[field] = value;

      setNodesValue(nodes.value);
    }

    function setNodesValue(
      nodes: ExpressionPredicateNodeTree<IWorkflowTransitionNode>
    ) {
      emit('input', nodes);
    }

    function convertNodeBranchFromSimpleExpressionToOneNodeExpression(
      branch: ExpressionPredicateNodeTree<IWorkflowTransitionNode>,
      currentNode: IWorkflowTransitionNode,
      subtype: TransitionNodeOperandSubtype
    ) {
      const startBracket = branch.shift() as IWorkflowTransitionNode;
      const closeBracket = branch.pop() as IWorkflowTransitionNode;

      // Create a new group node
      // this ID property is kept to keep the node record and not recreate the node unnecessarily

      currentNode.type = PredicateNodeType.Operand;
      currentNode.subtype = subtype;
      currentNode.groups = [];
      currentNode.constant = null;
      currentNode.threshold_group = null;

      currentNode.variable_name = '';

      return [startBracket, currentNode, closeBracket];
    }

    function convertNodeBranchFromOneNodeExpressionToSimpleExpression(
      branch: ExpressionPredicateNodeTree<IWorkflowTransitionNode>,
      currentNode: IWorkflowTransitionNode
    ) {
      const startBracket = branch.shift() as IWorkflowTransitionNode;
      const closeBracket = branch.pop() as IWorkflowTransitionNode;

      const equalComparisonNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operator,
        subtype: TransitionNodeBinaryOperators.Equals
      });

      const constantNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operand,
        subtype: TransitionNodeOperandSubtype.Constant,
        constant: null
      });

      currentNode.type = PredicateNodeType.Operand;
      currentNode.subtype = TransitionNodeOperandSubtype.Variable;
      currentNode.groups = [];
      currentNode.constant = null;
      currentNode.threshold_group = null;
      currentNode.variable_name = '';

      return [
        startBracket,
        currentNode,
        equalComparisonNode,
        constantNode,
        closeBracket
      ];
    }

    function insertConditionExpression(
      tree: ExpressionPredicateNodeTree<IWorkflowTransitionNode>,
      root: boolean
    ) {
      const StartBracketNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operator,
        subtype: TransitionNodeBinaryOperators.StartBracket
      });
      const VariableNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operand,
        subtype: TransitionNodeOperandSubtype.Variable,
        variable_name: ''
      });
      const EqualsNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operator,
        subtype: TransitionNodeBinaryOperators.Equals
      });
      const ConstantNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operand,
        subtype: TransitionNodeOperandSubtype.Constant,
        constant: null
      });
      const CloseBracketNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operator,
        subtype: TransitionNodeBinaryOperators.CloseBracket
      });

      const operatorNode = new WorkflowTransitionNode({
        type: PredicateNodeType.Operator,
        subtype: root
          ? TransitionNodeBinaryOperators.Or
          : TransitionNodeBinaryOperators.And
      });

      const nodeExpressionList = [
        StartBracketNode,
        VariableNode,
        EqualsNode,
        ConstantNode,
        CloseBracketNode
      ];

      if (tree.length > 0) {
        if (root) {
          tree.push(operatorNode);
        } else {
          tree.splice(tree.length - 1, 0, operatorNode);
        }
      }

      if (root) {
        tree.push([StartBracketNode, nodeExpressionList, CloseBracketNode]);
      } else {
        // add expression before the last node (before the closing bracket)
        tree.splice(tree.length - 1, 0, nodeExpressionList);
      }
    }

    function removeSimpleExpressionNodeGroup(
      tree: ExpressionPredicateNodeTree<IWorkflowTransitionNode>,
      groupIndex: number
    ) {
      let nodesList = [...tree];

      /** Current Node to render */
      const currentGroupExpression = tree[groupIndex];

      /** Previous Node to delete */
      const previousLogicalOperatorNode = tree[groupIndex - 1];

      /** Next Node to delete */
      const nextLogicalOperatorNode = tree[groupIndex + 1];

      if (currentGroupExpression && Array.isArray(currentGroupExpression)) {
        // Remove the group expression if exists
        nodesList.splice(groupIndex, 1);
      }

      if (
        previousLogicalOperatorNode &&
        !Array.isArray(previousLogicalOperatorNode) &&
        previousLogicalOperatorNode.isOperator()
      ) {
        // Remove the logical operator before the group if it exists
        nodesList.splice(groupIndex - 1, 1);
      } else if (
        nextLogicalOperatorNode &&
        !Array.isArray(nextLogicalOperatorNode) &&
        nextLogicalOperatorNode.isOperator()
      ) {
        // If there is no previous logical operator node, it means this is the first group
        // Check if there is a next logical operator node to remove

        // Remove the logical operator after the group if it exists
        nodesList.splice(groupIndex, 1);
      }

      // Hack because use v-model update is not working
      setNodesValue(nodesList);
    }

    function genNodeBranch() {
      let renderedNodes: VNodeChildren = [];

      let writeIndex = 0;

      for (let readIndex = 0; readIndex < nodes.value.length; readIndex++) {
        if (writeIndex == readIndex) {
          /** Current Node to render */
          const currentNode = nodes.value[readIndex];

          /** Next Node to render */
          const nextNode = nodes.value[readIndex + 1];

          /** After Next Node to render */
          const afterNextNode = nodes.value[readIndex + 2];

          // If currentNode in tree is another expression then rerender this component here
          if (Array.isArray(currentNode)) {
            // Handle nested group by recursion return entire component not just this minor tree

            const newGroup = h('v-row', { props: { dense: true } }, [
              h(
                'v-col',
                {
                  props: { cols: 12 }
                },
                [
                  h('div', { class: 'd-flex flex-row' }, [
                    h(
                      'v-card',
                      {
                        style: {
                          order: 1
                        },
                        class: 'pa-3 w-full',
                        props: {
                          flat: props.level == 0,
                          outlined: props.level == 0,
                          elevation: 1
                        }
                      },
                      [
                        h('WorkflowTransitionPredicateQueryTree', {
                          props: {
                            value: currentNode,
                            disabled: props.disabled,
                            level: props.level + 1,
                            fieldErrors: props.fieldErrors
                          },
                          on: {
                            input: (
                              value: ExpressionPredicateNodeTree<
                                IWorkflowTransitionNode
                              >
                            ) => {
                              nodes.value[readIndex] = value;

                              setNodesValue(nodes.value);
                            },
                            'adjust-current-node-group': (
                              subtype: TransitionNodeOperandSubtype,
                              currentNode: IWorkflowTransitionNode
                            ) => {
                              const currentBranch = nodes.value[
                                readIndex
                              ] as ExpressionPredicateNodeTree<
                                IWorkflowTransitionNode
                              >;

                              switch (subtype) {
                                case TransitionNodeOperandSubtype.OwnerGroup:
                                case TransitionNodeOperandSubtype.AssigneeGroup:
                                case TransitionNodeOperandSubtype.Threshold:
                                  {
                                    nodes.value[
                                      readIndex
                                    ] = convertNodeBranchFromSimpleExpressionToOneNodeExpression(
                                      currentBranch,
                                      currentNode,
                                      subtype
                                    );
                                  }
                                  setNodesValue(nodes.value);
                                  break;

                                case TransitionNodeOperandSubtype.Constant:
                                case TransitionNodeOperandSubtype.Variable:
                                default: {
                                  nodes.value[
                                    readIndex
                                  ] = convertNodeBranchFromOneNodeExpressionToSimpleExpression(
                                    currentBranch,
                                    currentNode
                                  );

                                  setNodesValue(nodes.value);
                                }
                              }
                            }
                          }
                        }),

                        props.level == 0 &&
                          h(
                            'v-col',
                            {
                              class: 'pb-0 px-0',
                              props: {
                                cols: 12,
                                align: 'center',
                                justify: 'center'
                              }
                            },
                            [
                              h(
                                'v-tooltip',
                                {
                                  props: {
                                    bottom: true
                                  },
                                  scopedSlots: {
                                    activator: ({ on, attrs }: any) =>
                                      h(
                                        'v-btn',
                                        {
                                          class: 'v-btn--app text-capitalize',
                                          props: {
                                            color: 'primary',
                                            outlined: true,
                                            small: true,
                                            disabled: props.disabled
                                          },
                                          on: {
                                            ...on,
                                            click: () =>
                                              insertConditionExpression(
                                                currentNode,
                                                false
                                              )
                                          },
                                          attrs
                                        },
                                        [
                                          h(
                                            'v-icon',
                                            {
                                              props: {
                                                left: true
                                              }
                                            },
                                            ['mdi-plus-circle']
                                          ),
                                          'Add Condition'
                                        ]
                                      )
                                  }
                                },
                                'Add Condition Group'
                              )
                            ]
                          )
                      ]
                    ),

                    // Remove group button
                    props.level === 0
                      ? h('v-tooltip', {
                          props: {
                            bottom: true
                          },
                          scopedSlots: {
                            activator: ({ on, attrs }) =>
                              h(
                                'v-btn',
                                {
                                  style: { right: '0px', order: 2 },
                                  class:
                                    'align-self-center justify-self-center ml-2',
                                  props: {
                                    icon: true,
                                    color: 'error',
                                    disabled: props.disabled
                                  },
                                  on: {
                                    ...on,
                                    click: () =>
                                      removeSimpleExpressionNodeGroup(
                                        nodes.value,
                                        readIndex
                                      )
                                  },
                                  attrs
                                },
                                [h('v-icon', ['mdi-minus-circle'])]
                              ),
                            default: () => {
                              return 'Remove Group';
                            }
                          }
                        })
                      : null
                  ])
                ]
              )
            ]);

            renderedNodes.push(newGroup);
            // Move forward with current list to render nodes
            writeIndex++;
          } else if (!currentNode.isCloseBracket()) {
            if (currentNode.isStartBracket()) {
              // if currentNode is a start bracket then continue start new group
              writeIndex++;
            } else if (currentNode.isOperator()) {
              // Handle logical operator
              renderedNodes.push(
                h(
                  'v-col',
                  {
                    props: { cols: 12, align: 'center', justify: 'center' },
                    class: { 'py-0': props.level > 0 }
                  },
                  [
                    props.level == 0 &&
                      h(
                        'div',
                        {
                          class:
                            'd-flex flex-row align-center justify-center position-relative'
                        },
                        [
                          h(
                            'v-btn-toggle',
                            {
                              props: {
                                value: currentNode.subtype,
                                rounded: true,
                                mandatory: true,
                                disabled: props.disabled,
                                dense: true,
                                outlined: true,
                                small: true,
                                backgroundColor:
                                  props.level > 0
                                    ? 'grey darken-2'
                                    : 'background',
                                dark: true
                              },
                              on: {
                                change: (
                                  value: TransitionNodeBinaryOperators
                                ) => {
                                  setNodeProperty(
                                    currentNode,
                                    'subtype',
                                    value
                                  );
                                }
                              }
                            },
                            [
                              h(
                                'v-btn',
                                {
                                  props: {
                                    value: TransitionNodeBinaryOperators.And,
                                    color:
                                      currentNode.subtype ==
                                      TransitionNodeBinaryOperators.And
                                        ? 'primary'
                                        : '',
                                    outlined:
                                      currentNode.subtype !=
                                      TransitionNodeBinaryOperators.And,
                                    small: true,
                                    disabled: props.level > 0 || props.disabled,
                                    light:
                                      currentNode.subtype !=
                                      TransitionNodeBinaryOperators.And
                                  }
                                },
                                [
                                  logicalOperatorStringRepresentation(
                                    TransitionNodeBinaryOperators.And
                                  )
                                ]
                              ),
                              props.level == 0
                                ? h(
                                    'v-btn',
                                    {
                                      props: {
                                        value: TransitionNodeBinaryOperators.Or,
                                        color:
                                          currentNode.subtype ==
                                          TransitionNodeBinaryOperators.Or
                                            ? 'primary'
                                            : '',
                                        outlined:
                                          currentNode.subtype !=
                                          TransitionNodeBinaryOperators.Or,
                                        small: true,
                                        disabled: props.disabled,
                                        light:
                                          currentNode.subtype !=
                                          TransitionNodeBinaryOperators.Or
                                      }
                                    },
                                    [
                                      logicalOperatorStringRepresentation(
                                        TransitionNodeBinaryOperators.Or
                                      )
                                    ]
                                  )
                                : null
                            ]
                          )
                        ]
                      )
                  ]
                )
              );
              writeIndex++;
            }
            // CHeck that the next next node is not a new group or an operator
            else if (!Array.isArray(nextNode) && nextNode.isOperator()) {
              // determine row and create group from here

              const currentOperandNodeSubtypeLabel =
                currentNode.subtype == TransitionNodeOperandSubtype.Constant
                  ? 'Constant'
                  : currentNode.subtype == TransitionNodeOperandSubtype.Variable
                  ? 'Property Name'
                  : currentNode.subtype ==
                    TransitionNodeOperandSubtype.Threshold
                  ? 'Property Name'
                  : 'Groups';

              const currentNodeOperand = h('v-text-field', {
                style: 'grid-column: span 1',
                props: {
                  label: currentOperandNodeSubtypeLabel,
                  outlined: true,
                  dense: true,
                  hideDetails: 'auto',
                  type:
                    currentNode.subtype == TransitionNodeOperandSubtype.Constant
                      ? 'number'
                      : 'text',
                  hint:
                    'This field should match the meta_label of the form field',
                  rules: [requiredValidator],
                  value:
                    currentNode.subtype == TransitionNodeOperandSubtype.Constant
                      ? currentNode.constant
                      : currentNode.variable_name,
                  errorMessages:
                    (props.fieldErrors[readIndex] &&
                      (currentNode.subtype ==
                      TransitionNodeOperandSubtype.Constant
                        ? props.fieldErrors[readIndex]['constant']
                        : props.fieldErrors[readIndex]['variable_name'])) ||
                    []
                },
                on: {
                  input: (value: string) => {
                    setNodeProperty(
                      currentNode,
                      currentNode.subtype ==
                        TransitionNodeOperandSubtype.Constant
                        ? 'constant'
                        : 'variable_name',
                      value
                    );
                  }
                }
              });

              const nextNodeOperator = h('v-select', {
                style: 'grid-column: span 1',
                props: {
                  label: 'Operator', //nextNode(operator)
                  outlined: true,
                  dense: true,
                  value: nextNode.subtype,
                  items: WorkflowTransitionNodeOperatorInfo.getWorkflowTransitionNodeOperatorInfoList(),
                  itemText: 'display_name',
                  itemValue: 'value',
                  rules: [requiredValidator],
                  hideDetails: 'auto',
                  errorMessages:
                    (props.fieldErrors[readIndex + 1] &&
                      props.fieldErrors[readIndex + 1]['subtype']) ||
                    []
                },
                on: {
                  change: (value: TransitionNodeOperandSubtype) =>
                    setNodeProperty(nextNode, 'subtype', value)
                },
                scopedSlots: {
                  selection: ({ item, attrs, disabled }) =>
                    h(
                      'div',
                      {
                        staticClass:
                          'v-select__selection v-select__selection--comma',
                        attrs
                      },
                      [
                        h(
                          'v-icon',
                          {
                            props: {
                              size: 18,
                              color: !disabled ? 'black' : 'text--disabled'
                            }
                          },
                          [item.icon]
                        )
                      ]
                    ),

                  item: ({ item, attrs }) => [
                    h(
                      'v-list-item-action',
                      {
                        class: 'mr-2'
                      },
                      [
                        h(
                          'v-icon',
                          {
                            props: {
                              size: 18,
                              color: !attrs.disabled
                                ? 'black'
                                : 'text--disabled'
                            }
                          },
                          [item.icon]
                        )
                      ]
                    ),

                    h('v-list-item-content', [
                      h('v-list-item-title', [item.display_name])
                    ])
                  ]
                }
              });

              const afterNodeOperator =
                afterNextNode &&
                !Array.isArray(afterNextNode) &&
                h('v-text-field', {
                  style: 'grid-column: span 1',
                  props: {
                    label: 'Value', //afterNextNode(operand - constant)
                    outlined: true,
                    dense: true,
                    value: afterNextNode.constant,
                    rules: [requiredValidator],
                    hideDetails: 'auto',
                    type:
                      currentNode.subtype ==
                      TransitionNodeOperandSubtype.Constant
                        ? 'number'
                        : 'text',

                    errorMessages:
                      (props.fieldErrors[readIndex + 2] &&
                        props.fieldErrors[readIndex + 2]['constant']) ||
                      []
                  },
                  on: {
                    input: (value: TransitionNodeOperandSubtype) =>
                      setNodeProperty(
                        afterNextNode,
                        'constant',
                        currentNode.subtype ==
                          TransitionNodeOperandSubtype.Constant
                          ? Number(value)
                          : value
                      )
                  }
                });

              const thresholdGroupOperand = h(
                WorkflowStateThresholdGroupAutoPicker,
                {
                  style: 'grid-column: span 1',
                  attrs: {
                    label: 'Threshold Group',
                    outlined: true,
                    dense: true,
                    hideDetails: 'auto',
                    rules: [requiredValidator],
                    hint: "Value must be ≤ assignee's highest threshold",
                    errorMessages:
                      (props.fieldErrors[readIndex] &&
                        props.fieldErrors[readIndex]['threshold_group']) ||
                      []
                  },
                  props: {
                    value: currentNode.threshold_group,
                    itemValue: 'id',
                    itemText: (item: IWorkflowState) => item.toString()
                  },
                  on: {
                    change: (value: (Group | number)[]) =>
                      setNodeProperty(currentNode, 'threshold_group', value)
                  }
                }
              );

              const groupNodeOperand = h(GroupAutoCompletePicker, {
                style: 'grid-column: span 3',
                attrs: {
                  label: 'Groups', //nextNode(operator)
                  outlined: true,
                  dense: true,
                  hideDetails: 'auto',
                  multiple: true,
                  chips: true,
                  smallChips: true,
                  deletableChips: true,
                  rules: [requiredValidator],
                  errorMessages:
                    (props.fieldErrors[readIndex] &&
                      props.fieldErrors[readIndex]['groups']) ||
                    []
                },
                props: {
                  value: currentNode.groups
                },
                on: {
                  change: (value: (Group | number)[]) =>
                    setNodeProperty(currentNode, 'groups', value)
                }
              });

              let currentGroupSubChildren: VNodeChildren = [];

              switch (currentNode.subtype) {
                case TransitionNodeOperandSubtype.OwnerGroup:
                case TransitionNodeOperandSubtype.AssigneeGroup:
                  currentGroupSubChildren = [groupNodeOperand];
                  break;
                case TransitionNodeOperandSubtype.Threshold:
                  currentGroupSubChildren = [
                    thresholdGroupOperand,

                    h('v-select', {
                      style: { 'grid-column': 'span 1' },
                      props: {
                        label: 'Operator',
                        outlined: true,
                        disabled: true,
                        dense: true,
                        value: TransitionNodeBinaryOperators.LessThanEqual,
                        items: [
                          WorkflowTransitionNodeOperatorInfo.LessThanEqual
                        ],
                        itemText: 'display_name',
                        itemValue: 'value',
                        hideDetails: 'auto'
                      },
                      scopedSlots: {
                        selection: ({ item, attrs, disabled }) =>
                          h(
                            'div',
                            {
                              staticClass:
                                'v-select__selection v-select__selection--comma',
                              attrs
                            },
                            [
                              h(
                                'v-icon',
                                {
                                  props: {
                                    size: 18,
                                    color: !disabled
                                      ? 'black'
                                      : 'text--disabled'
                                  }
                                },
                                [item.icon]
                              )
                            ]
                          )
                      }
                    }),
                    currentNodeOperand
                  ];

                  break;
                default:
                  currentGroupSubChildren = [
                    currentNodeOperand,
                    nextNodeOperator,
                    afterNodeOperator
                  ];
              }

              const currentGroupChildren: VNodeChildren = [
                h('v-select', {
                  style: 'grid-column: span 3',
                  on: {
                    change: (value: TransitionNodeOperandSubtype) => {
                      /** Should update UI Node Group */
                      let shouldUpdateNodeGroupUI = false;

                      /** Is current node either a threshold or a group that only shows one operand in that group UI */
                      const isNodeOneOperandExpression = [
                        TransitionNodeOperandSubtype.OwnerGroup,
                        TransitionNodeOperandSubtype.AssigneeGroup,
                        TransitionNodeOperandSubtype.Threshold
                      ].includes(
                        currentNode.subtype as TransitionNodeOperandSubtype
                      );

                      switch (value) {
                        case TransitionNodeOperandSubtype.OwnerGroup:
                        case TransitionNodeOperandSubtype.AssigneeGroup:
                        case TransitionNodeOperandSubtype.Threshold:
                          {
                            shouldUpdateNodeGroupUI = !isNodeOneOperandExpression;
                          }
                          break;

                        case TransitionNodeOperandSubtype.Variable:
                        case TransitionNodeOperandSubtype.Constant:
                        default:
                          {
                            shouldUpdateNodeGroupUI = isNodeOneOperandExpression;
                          }
                          break;
                      }

                      // Update the node before emitting to update the UI as current node is being used in the newly modified group
                      // This is to directly keep the ID of current node so as to not create new nodes unnecessarily
                      setNodeProperty(currentNode, 'subtype', value);

                      if (shouldUpdateNodeGroupUI) {
                        emit('adjust-current-node-group', value, currentNode);
                      }
                    }
                  },
                  props: {
                    label: 'Condition Type',
                    outlined: true,
                    dense: true,
                    value: currentNode.subtype,
                    items: WorkflowTransitionNodeOperandTypeInfo.getWorkflowTransitionNodeOperandTypeInfoList(),
                    rules: [requiredValidator],
                    itemText: 'display_name',
                    itemValue: 'value',
                    hideDetails: 'auto'
                  }
                }),

                currentGroupSubChildren
              ];

              renderedNodes.push(
                h(
                  'v-col',
                  {
                    class: 'pa-0',
                    props: { cols: 12, align: 'center', justify: 'center' }
                  },
                  [
                    h(
                      'div',
                      {
                        class: 'd-grid gap-8',
                        style: {
                          // Using fixed width to avoid grid jumping when the select changes width
                          // and maintaining consistent width amount of columns to avoid jumping
                          'grid-template-columns': '1fr 75px 1fr',
                          width: '100%'
                        }
                      },
                      currentGroupChildren
                    )
                  ]
                )
              );
              if (afterNextNode) {
                writeIndex += 3;
              } else {
                writeIndex += 2;
              }
            } else {
              writeIndex++;
            }
          }
        }
      }

      return renderedNodes;
    }

    return () =>
      h('div', [
        genNodeBranch(),

        props.level === 0
          ? h('v-row', { props: { dense: true } }, [
              h(
                'v-col',
                {
                  props: {
                    cols: 12
                  }
                },
                [
                  h(
                    'div',
                    { class: 'd-flex align-center justify-space-between' },
                    [
                      h(
                        'v-tooltip',
                        {
                          props: {
                            bottom: true
                          },
                          scopedSlots: {
                            activator: ({ on, attrs }: any) =>
                              h(
                                'v-btn',
                                {
                                  class: 'v-btn--app text-capitalize',
                                  props: {
                                    color: 'primary',
                                    outlined: true,
                                    small: true,
                                    disabled: props.disabled
                                  },
                                  on: {
                                    ...on,
                                    click: () =>
                                      insertConditionExpression(
                                        nodes.value,
                                        true
                                      )
                                  },
                                  attrs
                                },
                                [
                                  h(
                                    'v-icon',
                                    {
                                      props: {
                                        left: true
                                      }
                                    },
                                    ['mdi-plus-circle']
                                  ),
                                  'Add Condition Group'
                                ]
                              )
                          }
                        },
                        'Add Condition Group'
                      )
                    ]
                  )
                ]
              )
            ])
          : null
      ]);
  }
});
